import { ChatMessage } from "./types";

/**
 * Global manager for subthread messages.
 * This allows us to route SSE events from processSubthreadCreation to the correct SubthreadMessages component.
 */

type SubthreadState = {
  messages: ChatMessage[];
  isStreaming: boolean;
};

type Listener = (state: SubthreadState) => void;
type GlobalStreamingListener = (count: number) => void;

class SubthreadManager {
  private states = new Map<string, SubthreadState>();
  private listeners = new Map<string, Set<Listener>>();
  private globalStreamingListeners = new Set<GlobalStreamingListener>();

  getState(threadId: string): SubthreadState {
    if (!this.states.has(threadId)) {
      this.states.set(threadId, { messages: [], isStreaming: false });
    }

    return this.states.get(threadId)!;
  }

  setState(threadId: string, state: SubthreadState) {
    this.states.set(threadId, state);
    this.notifyListeners(threadId);
    this.notifyGlobalStreamingListeners();
  }

  updateMessages(threadId: string, messages: ChatMessage[]) {
    const currentState = this.getState(threadId);
    this.setState(threadId, { ...currentState, messages });
  }

  setStreaming(threadId: string, isStreaming: boolean) {
    const currentState = this.getState(threadId);
    this.setState(threadId, { ...currentState, isStreaming });
  }

  subscribe(threadId: string, listener: Listener): () => void {
    if (!this.listeners.has(threadId)) {
      this.listeners.set(threadId, new Set());
    }

    this.listeners.get(threadId)!.add(listener);

    // return unsubscribe function
    return () => {
      const listeners = this.listeners.get(threadId);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(threadId);
        }
      }
    };
  }

  getStreamingCount(): number {
    let count = 0;
    for (const state of this.states.values()) {
      if (state.isStreaming) {
        count++;
      }
    }

    return count;
  }

  subscribeToGlobalStreaming(listener: GlobalStreamingListener): () => void {
    this.globalStreamingListeners.add(listener);

    // immediately notify with current state
    listener(this.getStreamingCount());

    return () => {
      this.globalStreamingListeners.delete(listener);
    };
  }

  private notifyListeners(threadId: string) {
    const listeners = this.listeners.get(threadId);
    if (listeners) {
      const state = this.getState(threadId);
      listeners.forEach((listener) => listener(state));
    }
  }

  private notifyGlobalStreamingListeners() {
    const count = this.getStreamingCount();
    this.globalStreamingListeners.forEach((listener) => listener(count));
  }
}

// singleton instance
export const subthreadManager = new SubthreadManager();
